import { useContext, useMemo, useState } from "react";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import Phone from "@mui/icons-material/Phone";
import {
  Box,
  DialogContent,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { Formik } from "formik";
import { isEqual } from "lodash-es";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { Promisable } from "type-fest";
import { v4 } from "uuid";
import type { InferType } from "yup";
import { mixed, object, string } from "yup";

import { dayjs, NhsNumberSchema } from "@thalamos/common";
import type { ExtendedPatient, Patient } from "@thalamos/aspire-common";
import {
  formValidationErrorMessages,
  generateDateBeforeTodayValidationRule,
  isoDateString,
  isSoadUserSession,
  nameAndAddress,
  nonEmptyString,
} from "@thalamos/aspire-common";

import { api } from "../../api.js";
import { routeFns } from "../../routePaths.js";
import { ConfirmationModal } from "../ConfirmationModal.js";
import type { FieldProps } from "../FieldProps.js";
import {
  Banner,
  BannerList,
  FormTitle,
  PopupDialog,
  ReadOnlyContent,
  renderErrorToast,
  renderSuccessToast,
  TextField,
} from "~/components/design-system/index.js";
import {
  BranchFormField,
  FormFooterSection,
  NameAddressFormField,
  TextboxFormField,
} from "~/components/form/index.js";
import { Container } from "~/components/layout/styleWrappers.js";
import { LoggedInUserContext } from "~/Contexts.js";
import { useOdsOrganisation } from "~/hooks/useOdsOrganisation.js";
import { renderField } from "~/pages/FormDraft/ThalamosForm.js";

export type editPatientDialog = "editPatientDialog";

export const patientCreateSchema = object({
  givenName: nonEmptyString
    .required("Please enter patient's forename")
    .max(255, formValidationErrorMessages.tooManyCharactersError),
  familyNameAndAddress: nameAndAddress({
    nameRequiredError: "Please enter patient's surname",
    addressRequiredError: "Please enter patient's home address",
  }),
  isPatientDateOfBirth: mixed()
    .nullable()
    .oneOf([true, false, null], "Please select an option")
    .default(null)
    .required("Please select an option"),
  dateOfBirth: isoDateString.default(null).when("isPatientDateOfBirth", {
    is: true,
    then: () =>
      generateDateBeforeTodayValidationRule(
        "date",
        "Date of birth must not be in the future",
      ).required("Please enter patient's date of birth"),
  }),
  deathNotificationStatus: mixed()
    .nullable()
    .oneOf(["informal", "formal", "removed", null], "Please select an option")
    .default(null)
    .optional(),

  deceasedDateTime: string().nullable().optional(),
});

export type PatientCreateSchema = InferType<typeof patientCreateSchema>;

export type PatientCreateEditProps = {
  restricted?: boolean;
  nhsNumber?: string;
  refetchAndRedirect?: () => void;
  setShowUpdateSuccessDialog?: (v: boolean) => void;
  exitPatientCreate: () => void;
  onCreated?: (patientId: string) => Promisable<void>;
  initialState: PatientCreateSchema | null;
  mode: "create" | "edit" | editPatientDialog;
  expectedVersion?: number;
  existingPatientId?: string;
  existingPatient?: Patient;
};

const useSearchByNhsNumberEnabled = () => {
  const userContext = useContext(LoggedInUserContext);

  return (
    userContext?.user.sessionOrganisationConfiguration?.nhsNumberEnabled ??
    false
  );
};

export function PatientCreateEdit({
  refetchAndRedirect,
  setShowUpdateSuccessDialog,
  initialState,
  exitPatientCreate,
  onCreated,
  mode,
  expectedVersion,
  existingPatientId,
  existingPatient,
  nhsNumber,
}: PatientCreateEditProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const patientId = useMemo(() => existingPatientId || v4(), []);

  const userContext = useContext(LoggedInUserContext);
  const isSoad = isSoadUserSession(userContext?.user);

  const [confirmFn, setConfirmFn] = useState<{
    confirmFn: () => void;
    message: string;
  } | null>(null);

  const shouldShowNhsLabel = useSearchByNhsNumberEnabled();

  const [formChanged, setFormChanged] = useState(false);
  const [confirmInput, setConfirmInput] = useState("");
  const [isInputValid, setIsInputValid] = useState(false);
  const [hasConfirmed, setHasConfirmed] = useState(false);

  const handleFormChange = (values: PatientCreateSchema) => {
    let changedFieldsCount = 0;

    (Object.keys(values) as (keyof PatientCreateSchema)[]).forEach((key) => {
      const newValue = values[key];
      const oldValue = initialState?.[key];
      if (!isEqual(newValue, oldValue)) {
        changedFieldsCount++;
      }
    });

    setFormChanged(changedFieldsCount > 1);
  };

  const handleClose = () => {
    setFormChanged(false);
    navigate(routeFns.patientHome(patientId));
  };

  return (
    <>
      <FormTitle
        useReducedTopPadding={true}
        hasTitleBottomMargin={false}
        titleText={
          isSoad
            ? t("pages.soadAssessment.patientCreate.title")
            : mode === "create"
              ? t("pages.patientSearch.patientCreate.title")
              : `Edit patient ${initialState?.givenName} ${initialState?.familyNameAndAddress?.name} ${nhsNumber && shouldShowNhsLabel ? `(${NhsNumberSchema.catch(t("common.unknown")).parse(nhsNumber)})` : ""}`
        }
      />

      <PopupDialog
        open={formChanged && !hasConfirmed && mode !== "create"}
        onClose={() => {}}
        maxWidth="sm"
        fullScreen={false}
        hasPadding={true}
      >
        <DialogContent>
          <Banner
            sx={{ mb: 2 }}
            title={t("pages.patientEditCreate.confirmBanner", {
              patientSearchRoute: routeFns.patientSearch(),
            })}
            bannerType={BannerList.WARNING}
          />
          <TextField
            name="confirmInput"
            value={confirmInput}
            onChange={(val) => {
              setConfirmInput(val);
              if (val.toLowerCase() === "confirm") {
                setIsInputValid(true);
                // TODO - Add an alert to datadog?
              } else {
                setIsInputValid(false);
              }
            }}
            useFullWidth={true}
          />
        </DialogContent>
        <FormFooterSection
          customFooterBackgroundColor="common.white"
          saveLabel={t("buttonLabels.confirm")}
          disableSubmit={!isInputValid}
          onSave={() => {
            setFormChanged(false);
            setHasConfirmed(true);
          }}
          discardLabel={t("buttonLabels.close")}
          onCancel={handleClose}
        />
      </PopupDialog>

      {existingPatient?.restricted && (
        <Banner
          sx={{ mb: 2 }}
          bannerType={BannerList.ERROR}
          title="Patient is marked as restricted in NHS National Systems. Address details are sensitive and not synchronised to/from other systems"
        />
      )}
      {mode !== "create" && !isSoad && (
        <Banner
          sx={{ mb: 2 }}
          bannerType={BannerList.WARNING}
          title={t("pages.patientEditCreate.editPatientInformation", {
            givenName:
              initialState?.givenName && initialState?.givenName.length > 0
                ? initialState?.givenName
                : t("common.unknown").toUpperCase(),
            familyName:
              initialState?.familyNameAndAddress &&
              initialState?.familyNameAndAddress?.name
                ? initialState?.familyNameAndAddress?.name
                : t("common.unknown").toUpperCase(),
            patientSearchRoute: routeFns.patientSearch(),
          })}
        />
      )}

      {confirmFn && (
        <ConfirmationModal
          message={confirmFn.message}
          confirmFn={confirmFn.confirmFn}
          closeFn={() => setConfirmFn(null)}
        />
      )}

      <Formik<any>
        validationSchema={patientCreateSchema}
        initialValues={{
          givenName: initialState?.givenName ?? null,
          familyNameAndAddress: {
            name: initialState?.familyNameAndAddress?.name ?? null,
            postalCode: initialState?.familyNameAndAddress?.postalCode ?? "",
            address: initialState?.familyNameAndAddress?.address ?? null,
            isConfirmed:
              initialState?.familyNameAndAddress?.isConfirmed ?? false,
          },
          isPatientDateOfBirth:
            typeof initialState?.isPatientDateOfBirth === "boolean"
              ? initialState?.isPatientDateOfBirth
              : null,
          dateOfBirth: initialState?.dateOfBirth ?? null,
          deathNotificationStatus:
            existingPatient?.deathNotificationStatus ?? null,
          deceasedDateTime: existingPatient?.deceasedDateTime ?? null,
        }}
        onSubmit={async (values: any) => {
          if (mode === "create") {
            const result = await api.patients.create(patientId, {
              type: "demographics",
              demographics: {
                name: {
                  given: values.givenName,
                  family: values.familyNameAndAddress.name,
                },
                address: {
                  address: values.familyNameAndAddress.address,
                  postalCode: values.familyNameAndAddress.postalCode,
                },
                dateOfBirth: values.dateOfBirth,
              },
            });

            if (result.status !== 200) {
              renderErrorToast({
                message: t("pages.patientEditCreate.createError"),
              });
            } else {
              renderSuccessToast({
                message: t("pages.patientEditCreate.createSuccess"),
              });
              if (onCreated) {
                await onCreated(patientId);
              } else {
                navigate(routeFns.patientHome(patientId));
              }
            }
          } else {
            setConfirmFn({
              message: t(`pages.patientEditCreate.editPatientConfirmation`, {
                givenName: initialState?.givenName,
                familyName: initialState?.familyNameAndAddress?.name,
              }),
              confirmFn: async () => {
                const result = await api.patients.update(patientId, {
                  expectedVersion: expectedVersion!,
                  type: "demographics",
                  demographics: {
                    name: {
                      given: values.givenName,
                      family: values.familyNameAndAddress.name,
                    },
                    address: {
                      address: values.familyNameAndAddress.address,
                      postalCode: values.familyNameAndAddress.postalCode,
                    },
                    dateOfBirth: values.dateOfBirth,
                  },
                  deathNotificationStatus: values.deathNotificationStatus,

                  // Blank out the deceased date time if death notification status is not formal/informal
                  ...(["formal", "informal"].includes(
                    values.deathNotificationStatus,
                  )
                    ? {
                        deceasedDateTime:
                          initialState?.deceasedDateTime ?? null,
                      }
                    : { deceasedDateTime: null }),
                });

                if (result.status !== 200) {
                  renderErrorToast({
                    message: t("pages.patientEditCreate.updateError"),
                  });
                } else {
                  renderSuccessToast({
                    message: t("pages.patientEditCreate.updateSuccess"),
                  });
                  mode === "editPatientDialog"
                    ? refetchAndRedirect && refetchAndRedirect()
                    : setShowUpdateSuccessDialog &&
                      setShowUpdateSuccessDialog(true);
                }
                setConfirmFn(null);
              },
            });
          }
        }}
      >
        {({
          values,
          setValues,
          errors,
          touched,
          setFieldTouched,
          handleBlur,
          setValues: formikSetValues,
          submitForm,
          isSubmitting,
        }) => {
          const fieldProps = {
            validationSchema: patientCreateSchema,
            context: {},
            values,
            errors,
            handleBlur,
            setFieldTouched,
            touched,
            setValues,
            onChange: mode !== "create" && handleFormChange(values),
          };
          return (
            <>
              <PatientCreateFormFields
                fieldProps={fieldProps}
                values={values}
                setValues={formikSetValues}
                existingPatient={existingPatient}
                nhsNumber={nhsNumber}
              />
              <FormFooterSection
                customFooterBackgroundColor={theme.palette.common.white}
                onSave={submitForm}
                saveLabel={
                  mode === "create"
                    ? t("buttonLabels.createPatient")
                    : t("buttonLabels.updatePatient")
                }
                saveVariant="contained"
                discardLabel={
                  isSoad
                    ? t("buttonLabels.goBack")
                    : mode === "create"
                      ? t("buttonLabels.backSearch")
                      : mode === "editPatientDialog"
                        ? t("buttonLabels.close")
                        : t("buttonLabels.backPatient")
                }
                onCancel={exitPatientCreate}
                disableDiscard={isSubmitting}
                disableSubmit={isSubmitting}
                minHeight
              />
            </>
          );
        }}
      </Formik>
    </>
  );
}

export type PatientCreateFormFieldsProps = {
  fieldProps: FieldProps<PatientCreateSchema>;
  values: PatientCreateSchema;
  setValues: (values: PatientCreateSchema) => void;
  // rioEthnicity lives on ExtendedPatient; it stays optional here so callers
  // that only have a base Patient remain valid.
  existingPatient?: Patient & Pick<ExtendedPatient, "rioEthnicity">;
  nhsNumber?: string | null;
};

export function PatientCreateFormFields({
  fieldProps,
  values,
  setValues,
  existingPatient,
  nhsNumber,
}: PatientCreateFormFieldsProps) {
  const { t } = useTranslation();

  const userContext = useContext(LoggedInUserContext);
  const { user } = userContext!;

  const isSoad = isSoadUserSession(user);

  const addressLabel = isSoad
    ? t(
        "pages.patientSearch.patientCreate.familyNameAndAddress.soadAddressLabel",
      )
    : t("pages.patientSearch.patientCreate.familyNameAndAddress.addressLabel");

  return (
    <Container>
      {isSoad && nhsNumber && (
        <Box
          sx={{
            width: "100%",
            mb: 2,
          }}
        >
          <ReadOnlyContent
            label={"NHS number"}
            content={[nhsNumber]}
            useMarginBottom={true}
          ></ReadOnlyContent>
        </Box>
      )}
      <Box
        sx={{
          width: "100%",
          mb: 1,
        }}
      >
        <TextboxFormField
          field={{
            type: "textbox",
            field: "givenName",
            label: t("pages.patientSearch.patientCreate.givenName"),
          }}
          fieldProps={fieldProps}
        />
      </Box>
      <Box
        sx={{
          width: "100%",
          mb: 2,
        }}
      >
        <NameAddressFormField
          field={{
            type: "name-address",
            field: "familyNameAndAddress",
            disableEmail: true,
            nameLabel: t(
              "pages.patientSearch.patientCreate.familyNameAndAddress.nameLabel",
            ),
            addressLabel: addressLabel,
          }}
          fieldProps={fieldProps}
          nfaEnabled={isSoad}
        />
      </Box>
      <Box
        sx={{
          width: "100%",
        }}
      >
        <BranchFormField
          field={{
            type: "branch",
            field: "isPatientDateOfBirth",
            label: t("pages.patientSearch.patientCreate.isDOBlabel"),
            branches: [
              {
                label: t("common.yes"),
                fieldValue: true,
                fields: [
                  {
                    type: "date",
                    field: "dateOfBirth",
                    label: t("pages.patientSearch.patientCreate.dateOfBirth"),
                    maximum: (today) => today,
                  },
                ],
                fieldsOwned: ["dateOfBirth"],
              },
              {
                label: t("common.no"),
                fieldValue: false,
                fields: [],
                fieldsOwned: [],
              },
            ],
          }}
          fieldProps={fieldProps}
          renderField={renderField}
        />
      </Box>
      {existingPatient?.deathNotificationStatus && (
        <Box
          sx={{
            width: "100%",
          }}
        >
          <FormLabel
            sx={{ fontWeight: "bold", color: "text.primary" }}
            id="death-notification-status-radio-buttons-group-label"
          >
            {t(
              "pages.patientSearch.patientCreate.deathNotificationStatusLabel",
            )}
          </FormLabel>
          <RadioGroup
            aria-labelledby="death-notification-status-radio-buttons-group-label"
            name="radio-buttons-group"
            defaultValue={null}
            value={values.deathNotificationStatus}
            onChange={(event) => {
              setValues({
                ...values,
                deathNotificationStatus: event.target.value,
              });
            }}
          >
            <Stack
              sx={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <FormControlLabel
                control={<Radio sx={{ color: "primary.main" }} />}
                label={t(
                  "pages.patientSearch.patientCreate.deathNotificationStatusOptions.informal",
                )}
                value={"informal"}
              />
              <FormControlLabel
                control={<Radio sx={{ color: "primary.main" }} />}
                label={t(
                  "pages.patientSearch.patientCreate.deathNotificationStatusOptions.formal",
                )}
                value={"formal"}
              />
              <FormControlLabel
                control={<Radio sx={{ color: "primary.main" }} />}
                label={t(
                  "pages.patientSearch.patientCreate.deathNotificationStatusOptions.deathStatusRemoved",
                )}
                value={"removed"}
              />
            </Stack>
          </RadioGroup>
        </Box>
      )}
      {user.sessionOrganisationConfiguration?.gpPracticeDisplayEnabled &&
        existingPatient?.registeredGpOdsCode && (
          <GpPracticeReadOnly odsCode={existingPatient.registeredGpOdsCode} />
        )}
      {user.sessionOrganisationConfiguration?.officialGenderDisplayEnabled &&
        existingPatient?.pdsOfficialDocumentGender && (
          <OfficialGenderReadOnly
            gender={existingPatient.pdsOfficialDocumentGender}
          />
        )}
      {user.sessionOrganisationConfiguration?.rioEthnicityDisplayEnabled &&
        existingPatient?.rioEthnicity &&
        (existingPatient.rioEthnicity.display ||
          existingPatient.rioEthnicity.code) && (
          <RioEthnicityReadOnly rioEthnicity={existingPatient.rioEthnicity} />
        )}
    </Container>
  );
}

const officialGenderLabelKeys = {
  male: "pages.patientEditCreate.officialGender.options.male",
  female: "pages.patientEditCreate.officialGender.options.female",
  other: "pages.patientEditCreate.officialGender.options.other",
  unknown: "pages.patientEditCreate.officialGender.options.unknown",
} as const;

function OfficialGenderReadOnly({
  gender,
}: {
  gender: NonNullable<Patient["pdsOfficialDocumentGender"]>;
}) {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Box
      sx={{
        width: "100%",
        mt: 2,
        p: 2,
        borderRadius: 1,
        backgroundColor: theme.palette.grey[50],
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          mb: 1,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, color: "text.primary" }}
        >
          {t("pages.patientEditCreate.officialGender.title")}
        </Typography>
        <Tooltip arrow title={t("pages.patientEditCreate.officialGender.info")}>
          <InfoOutlined
            sx={{
              fontSize: "1rem",
              color: "text.secondary",
              cursor: "pointer",
            }}
          />
        </Tooltip>
      </Box>
      <Typography variant="body2" sx={{ color: "text.primary" }}>
        {t(officialGenderLabelKeys[gender])}
      </Typography>
    </Box>
  );
}

function RioEthnicityReadOnly({
  rioEthnicity,
}: {
  rioEthnicity: NonNullable<ExtendedPatient["rioEthnicity"]>;
}) {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Box
      sx={{
        width: "100%",
        mt: 2,
        p: 2,
        borderRadius: 1,
        backgroundColor: theme.palette.grey[50],
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          mb: 1,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, color: "text.primary" }}
        >
          {t("pages.patientEditCreate.rioEthnicity.title")}
        </Typography>
        <Tooltip arrow title={t("pages.patientEditCreate.rioEthnicity.info")}>
          <InfoOutlined
            sx={{
              fontSize: "1rem",
              color: "text.secondary",
              cursor: "pointer",
            }}
          />
        </Tooltip>
      </Box>
      <Typography variant="body2" sx={{ color: "text.primary" }}>
        {/* Fall back to the raw Rio code when no human-readable display value
            was provided, so a present code is never shown as blank. */}
        {rioEthnicity.display ?? rioEthnicity.code}
      </Typography>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        {t("pages.patientEditCreate.rioEthnicity.source", {
          rioInstanceName:
            rioEthnicity.rioInstanceName ??
            t("pages.patientEditCreate.rioEthnicity.unknownInstance"),
          lastPulledAt: dayjs(rioEthnicity.lastPulledAt).format("D MMM YYYY"),
        })}
      </Typography>
    </Box>
  );
}

function GpPracticeReadOnly({ odsCode }: { odsCode: string }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { organisation, loading } = useOdsOrganisation(odsCode);

  const formatAddress = () => {
    if (loading || !organisation) return null;
    const parts = [
      organisation.addressLine1,
      organisation.town,
      organisation.postCode,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : null;
  };

  const practiceName = loading
    ? t("pages.patientEditCreate.gpPractice.loading")
    : (organisation?.name ?? null);
  const address = formatAddress();

  return (
    <Box
      sx={{
        width: "100%",
        mt: 2,
        p: 2,
        borderRadius: 1,
        backgroundColor: theme.palette.grey[50],
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          mb: 1,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, color: "text.primary" }}
        >
          {t("pages.patientEditCreate.gpPractice.title")}
        </Typography>
        <Tooltip arrow title={t("pages.patientEditCreate.gpPractice.info")}>
          <InfoOutlined
            sx={{
              fontSize: "1rem",
              color: "text.secondary",
              cursor: "pointer",
            }}
          />
        </Tooltip>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 3,
        }}
      >
        <GpField
          label={t("pages.patientEditCreate.gpPractice.odsCode")}
          value={odsCode}
        />
        {practiceName && (
          <GpField
            label={t("pages.patientEditCreate.gpPractice.practiceName")}
            value={practiceName}
          />
        )}
        {address && (
          <GpField
            label={t("pages.patientEditCreate.gpPractice.practiceAddress")}
            value={address}
          />
        )}
        {organisation?.telephone && (
          <Box>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", display: "block" }}
            >
              {t("pages.patientEditCreate.gpPractice.telephone")}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" sx={{ color: "text.primary" }}>
                {organisation.telephone}
              </Typography>
              <Tooltip
                arrow
                title={t("pages.patientEditCreate.gpPractice.call")}
              >
                <a
                  href={`tel:${organisation.telephone}`}
                  aria-label={t("pages.patientEditCreate.gpPractice.call")}
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <Phone
                    sx={{
                      fontSize: "1.1rem",
                      color: "primary.main",
                      cursor: "pointer",
                    }}
                  />
                </a>
              </Tooltip>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}

function GpField({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography
        variant="caption"
        sx={{ color: "text.secondary", display: "block" }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: "text.primary" }}>
        {value}
      </Typography>
    </Box>
  );
}
